import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, FileText, Clock, CheckCircle, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ComplaintsPage() {
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Complaint submitted successfully. Reference ID: BRT' + Math.random().toString(36).substr(2, 9).toUpperCase());
  };

  const complaintCategories = [
    'Transaction Issues',
    'Account Access Problems',
    'Unauthorized Transactions',
    'Poor Customer Service',
    'Technical Issues',
    'Loan/Credit Issues',
    'Card Issues',
    'Other'
  ];

  const resolutionProcess = [
    { step: '1', title: 'Submit Complaint', desc: 'Fill out the complaint form with all relevant details', icon: <FileText className="w-8 h-8" /> },
    { step: '2', title: 'Acknowledgment', desc: 'Receive confirmation within 24 hours', icon: <Clock className="w-8 h-8" /> },
    { step: '3', title: 'Investigation', desc: 'Our team investigates your complaint (3-5 business days)', icon: <AlertCircle className="w-8 h-8" /> },
    { step: '4', title: 'Resolution', desc: 'Receive resolution and compensation if applicable', icon: <CheckCircle className="w-8 h-8" /> }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
            Complaints Management
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We take your concerns seriously. Submit a complaint and we'll resolve it promptly and fairly.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-slate-900 mb-4">Our Complaint Resolution Process</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We follow a transparent 4-step process to ensure your complaint is handled efficiently
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {resolutionProcess.map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center mx-auto mb-4">
                    {item.icon}
                  </div>
                  <div className="text-4xl font-bold text-brand-blue mb-2">{item.step}</div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
                {idx < resolutionProcess.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-blue-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-2 text-center">Submit a Complaint</h2>
            <p className="text-slate-600 mb-8 text-center">All fields are required</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input id="fullName" required className="mt-2 h-12 rounded-lg" />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" type="email" required className="mt-2 h-12 rounded-lg" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input id="phone" type="tel" required className="mt-2 h-12 rounded-lg" />
                </div>
                <div>
                  <Label htmlFor="accountNumber">Account Number</Label>
                  <Input id="accountNumber" required className="mt-2 h-12 rounded-lg" />
                </div>
              </div>

              <div>
                <Label htmlFor="category">Complaint Category</Label>
                <Select required>
                  <SelectTrigger className="mt-2 h-12 rounded-lg">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {complaintCategories.map((cat) => (
                      <SelectItem key={cat} value={cat.toLowerCase().replace(/\s+/g, '-')}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="transactionRef">Transaction Reference (if applicable)</Label>
                <Input id="transactionRef" placeholder="BRT123456789" className="mt-2 h-12 rounded-lg" />
              </div>

              <div>
                <Label htmlFor="complaintDetails">Complaint Details</Label>
                <Textarea id="complaintDetails" required rows={6} placeholder="Please provide as much detail as possible about your complaint..." className="mt-2 rounded-lg" />
              </div>

              <div>
                <Label htmlFor="dateIncident">Date of Incident</Label>
                <Input id="dateIncident" type="date" required className="mt-2 h-12 rounded-lg" />
              </div>

              <div className="bg-indigo-50 rounded-xl p-4 border border-blue-100">
                <p className="text-sm text-slate-700">
                  <strong>Note:</strong> By submitting this complaint, you agree to our investigation process. You will receive a reference number via email and SMS.
                </p>
              </div>

              <Button type="submit" className="w-full bg-brand-blue text-white hover:bg-[#1a1466] rounded-xl h-14 text-lg font-semibold">
                Submit Complaint
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 text-center">
              <Phone className="w-12 h-12 text-brand-blue mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">24/7 Helpline</h3>
              <p className="text-slate-600 mb-4">For urgent complaints</p>
              <p className="text-2xl font-bold text-brand-blue">0800-BRITEDGE</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 text-center">
              <Clock className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Response Time</h3>
              <p className="text-slate-600 mb-4">First response within</p>
              <p className="text-2xl font-bold text-green-600">24 Hours</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 text-center">
              <CheckCircle className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Resolution Time</h3>
              <p className="text-slate-600 mb-4">Average resolution</p>
              <p className="text-2xl font-bold text-purple-600">3-5 Days</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-8">Your Rights as a Customer</h2>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">✓</div>
              <p className="text-slate-700">Right to fair treatment and prompt resolution of complaints</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">✓</div>
              <p className="text-slate-700">Right to receive acknowledgment within 24 hours of complaint submission</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">✓</div>
              <p className="text-slate-700">Right to escalate to CBN Consumer Protection Department if unsatisfied</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">✓</div>
              <p className="text-slate-700">Right to compensation for losses due to service failures</p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 mt-1">✓</div>
              <p className="text-slate-700">Right to privacy and confidentiality of complaint details</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold font-heading mb-4">Need to Escalate?</h2>
          <p className="text-xl text-blue-100 mb-8">
            If you're not satisfied with our resolution, you can escalate to the Central Bank of Nigeria
          </p>
          <div className="space-y-2 text-blue-100">
            <p><strong>CBN Consumer Protection Department</strong></p>
            <p>Email: cpd@cbn.gov.ng</p>
            <p>Phone: +234 (0) 700 CALL CBN</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
